<template>
  <div class="obs-page">

    <!-- ── Cabeçalho ─────────────────────────────────────── -->
    <div class="obs-header">
      <div>
        <h1 class="obs-title">Observabilidade</h1>
        <p class="obs-sub">Monitore erros e momentos de ativação dos usuários</p>
      </div>
      <div class="obs-header-actions">
        <button class="obs-btn obs-btn--ghost" @click="loadAll" :disabled="loading">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="{ 'obs-spin': loading }"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
          Atualizar
        </button>
      </div>
    </div>

    <!-- ── Tabs ──────────────────────────────────────────── -->
    <div class="obs-tabs">
      <button
        class="obs-tab"
        :class="{ 'obs-tab--active': activeTab === 'erros' }"
        @click="activeTab = 'erros'"
      >
        ⚠️ Erros & Dificuldades
      </button>
      <button
        class="obs-tab"
        :class="{ 'obs-tab--active': activeTab === 'aha' }"
        @click="activeTab = 'aha'; loadAhaMoments()"
      >
        🚀 Aha Moments
      </button>
    </div>

    <!-- ── ABA: AHA MOMENTS ──────────────────────────────── -->
    <div v-if="activeTab === 'aha'">
      <div class="obs-aha-funnel">
        <div class="obs-aha-step" v-for="(s, i) in ahaSteps" :key="s.key">
          <div class="obs-aha-step__icon" :style="{ background: s.color }">{{ s.icon }}</div>
          <div class="obs-aha-step__body">
            <div class="obs-aha-step__label">{{ s.label }}</div>
            <div class="obs-aha-step__value">{{ ahaStats[s.key] || 0 }}</div>
            <div class="obs-aha-step__pct">
              {{ ahaStats.cadastrados ? Math.round((ahaStats[s.key] || 0) / ahaStats.cadastrados * 100) : 0 }}%
            </div>
          </div>
          <div v-if="i < ahaSteps.length - 1" class="obs-aha-arrow">→</div>
        </div>
      </div>

      <div class="obs-card obs-card--full">
        <div class="obs-card-head">
          <span class="obs-card-title">📊 Jornada dos Usuários</span>
          <div class="obs-filters">
            <select v-model="ahaFilter" class="obs-select">
              <option value="">Todos</option>
              <option value="activated">✅ Ativados</option>
              <option value="not_activated">⏳ Não ativados</option>
              <option value="no_video">Sem primeiro vídeo</option>
              <option value="no_chat">Sem primeiro chat</option>
              <option value="no_play">Vídeo nunca tocou</option>
              <option value="no_lead">Chat sem leads</option>
            </select>
          </div>
        </div>

        <div v-if="ahaLoading" class="obs-loading">Carregando…</div>
        <div v-else-if="!filteredAhaUsers.length" class="obs-empty">Nenhum usuário encontrado</div>
        <div v-else class="obs-table-wrap">
          <table class="obs-table">
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Site</th>
                <th>Último Login</th>
                <th class="obs-th-center">🎬 1º Vídeo</th>
                <th class="obs-th-center">💬 1º Chat</th>
                <th class="obs-th-center">▶️ Vídeo Tocado</th>
                <th class="obs-th-center">🎯 Lead Capturado</th>
                <th class="obs-th-center">Score</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in filteredAhaUsers" :key="u.id" :class="{ 'obs-row-activated': u.activated }">
                <td>
                  <div class="obs-user-info">
                    <div class="obs-user-email">{{ u.nome }}</div>
                    <div v-if="u.email" class="obs-user-email-text">{{ u.email }}</div>
                  </div>
                </td>
                <td>
                  <a v-if="u.site" :href="u.site.startsWith('http') ? u.site : 'https://' + u.site" target="_blank" class="obs-site-link">
                    {{ u.site.replace(/^https?:\/\//, '').replace(/\/$/, '') }}
                  </a>
                  <span v-else class="obs-empty-cell">—</span>
                </td>
                <td>
                  <span v-if="u.ultimo_login" class="obs-date-cell">{{ formatRelative(u.ultimo_login) }}</span>
                  <span v-else class="obs-empty-cell">Nunca logou</span>
                </td>
                <td class="obs-th-center">
                  <span v-if="u.milestones.first_video.reached" class="obs-check obs-check--ok" :title="formatDate(u.milestones.first_video.at)">✓</span>
                  <span v-else class="obs-check obs-check--no">—</span>
                </td>
                <td class="obs-th-center">
                  <span v-if="u.milestones.first_chat.reached" class="obs-check obs-check--ok" :title="formatDate(u.milestones.first_chat.at)">✓</span>
                  <span v-else class="obs-check obs-check--no">—</span>
                </td>
                <td class="obs-th-center">
                  <span v-if="u.milestones.video_embedded.reached" class="obs-check obs-check--ok">{{ u.milestones.video_embedded.count }}</span>
                  <span v-else class="obs-check obs-check--no">—</span>
                </td>
                <td class="obs-th-center">
                  <span v-if="u.milestones.chat_embedded.reached" class="obs-check obs-check--ok">{{ u.milestones.chat_embedded.count }}</span>
                  <span v-else class="obs-check obs-check--no">—</span>
                </td>
                <td class="obs-th-center">
                  <div class="obs-score">
                    <span class="obs-score__bar">
                      <span class="obs-score__fill" :style="{ width: (u.score / 5 * 100) + '%' }"></span>
                    </span>
                    <span class="obs-score__num">{{ u.score }}/5</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ── ABA: ERROS (conteúdo original) ────────────────── -->
    <div v-show="activeTab === 'erros'">

    <!-- ── KPI Cards ──────────────────────────────────────── -->
    <div class="obs-kpis">
      <div class="obs-kpi obs-kpi--red">
        <div class="obs-kpi-icon">⚠️</div>
        <div class="obs-kpi-body">
          <div class="obs-kpi-value">{{ summary.totals ? summary.totals.errors || 0 : '–' }}</div>
          <div class="obs-kpi-label">Erros (7 dias)</div>
        </div>
      </div>
      <div class="obs-kpi obs-kpi--orange">
        <div class="obs-kpi-icon">🔔</div>
        <div class="obs-kpi-body">
          <div class="obs-kpi-value">{{ summary.totals ? summary.totals.warnings || 0 : '–' }}</div>
          <div class="obs-kpi-label">Warnings (7 dias)</div>
        </div>
      </div>
      <div class="obs-kpi obs-kpi--blue">
        <div class="obs-kpi-icon">📅</div>
        <div class="obs-kpi-body">
          <div class="obs-kpi-value">{{ summary.totals ? summary.totals.today || 0 : '–' }}</div>
          <div class="obs-kpi-label">Erros hoje</div>
        </div>
      </div>
      <div class="obs-kpi obs-kpi--purple">
        <div class="obs-kpi-icon">👥</div>
        <div class="obs-kpi-body">
          <div class="obs-kpi-value">{{ summary.affected_users ? summary.affected_users.length : '–' }}</div>
          <div class="obs-kpi-label">Usuários afetados</div>
        </div>
      </div>
    </div>

    <div class="obs-grid">

      <!-- ── Top Erros ─────────────────────────────────────── -->
      <div class="obs-card obs-card--wide">
        <div class="obs-card-head">
          <span class="obs-card-title">🔥 Erros mais frequentes (7 dias)</span>
        </div>
        <div v-if="!summary.top_errors || !summary.top_errors.length" class="obs-empty">Nenhum erro registrado</div>
        <div v-else class="obs-table-wrap">
          <table class="obs-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Mensagem</th>
                <th>Categoria</th>
                <th>Ocorrências</th>
                <th>Usuários afetados</th>
                <th>Último</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(e, i) in summary.top_errors" :key="i">
                <td class="obs-rank">{{ i + 1 }}</td>
                <td class="obs-msg" :title="e.message">{{ truncate(e.message, 80) }}</td>
                <td><span class="obs-badge" :class="'obs-badge--' + e.category">{{ e.category }}</span></td>
                <td class="obs-count">{{ e.count }}</td>
                <td class="obs-count">{{ e.affected_users }}</td>
                <td class="obs-date">{{ formatDate(e.last_seen) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Por Categoria ──────────────────────────────────── -->
      <div class="obs-card">
        <div class="obs-card-head">
          <span class="obs-card-title">📂 Por categoria</span>
        </div>
        <div v-if="!summary.by_category || !summary.by_category.length" class="obs-empty">Sem dados</div>
        <div v-else class="obs-cat-list">
          <div v-for="c in summary.by_category" :key="c.category" class="obs-cat-row">
            <div class="obs-cat-name">
              <span class="obs-badge" :class="'obs-badge--' + c.category">{{ c.category }}</span>
            </div>
            <div class="obs-cat-bar-wrap">
              <div class="obs-cat-bar" :style="{ width: barWidth(c.count) + '%' }"></div>
            </div>
            <div class="obs-cat-count">{{ c.count }}</div>
          </div>
        </div>
      </div>

      <!-- ── Usuários afetados ───────────────────────────────── -->
      <div class="obs-card">
        <div class="obs-card-head">
          <span class="obs-card-title">👤 Usuários com mais erros</span>
        </div>
        <div v-if="!summary.affected_users || !summary.affected_users.length" class="obs-empty">Nenhum</div>
        <ul v-else class="obs-user-list">
          <li v-for="u in summary.affected_users" :key="u.user_id" class="obs-user-row">
            <div class="obs-user-avatar">{{ initials(u.user_name && u.user_name.trim() ? u.user_name : u.user_email) }}</div>
            <div class="obs-user-info">
              <div class="obs-user-email">{{ u.user_name && u.user_name.trim() ? u.user_name : (u.user_email || 'Usuário #' + u.user_id) }}</div>
              <div v-if="u.user_email && u.user_email !== (u.user_name || '').trim()" class="obs-user-email-text">{{ u.user_email }}</div>
              <div class="obs-user-meta">{{ u.total_errors }} erros · último {{ formatDate(u.last_error) }}</div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- ── Log Feed ───────────────────────────────────────── -->
    <div class="obs-card obs-card--full">
      <div class="obs-card-head obs-card-head--flex">
        <span class="obs-card-title">📋 Log em tempo real</span>
        <div class="obs-filters">
          <select v-model="filters.level" @change="loadLogs" class="obs-select">
            <option value="">Todos os níveis</option>
            <option value="error">Erro</option>
            <option value="warn">Warning</option>
            <option value="info">Info</option>
          </select>
          <select v-model="filters.category" @change="loadLogs" class="obs-select">
            <option value="">Todas as categorias</option>
            <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
          </select>
          <input v-model="filters.search" @keyup.enter="loadLogs" placeholder="Buscar mensagem..." class="obs-input" />
          <button class="obs-btn obs-btn--sm" @click="loadLogs">Filtrar</button>
        </div>
      </div>

      <div v-if="logsLoading" class="obs-loading">Carregando…</div>
      <div v-else-if="!logs.length" class="obs-empty">Nenhum log encontrado</div>
      <div v-else class="obs-table-wrap">
        <table class="obs-table obs-table--logs">
          <thead>
            <tr>
              <th>Data/Hora</th>
              <th>Nível</th>
              <th>Categoria</th>
              <th>Mensagem</th>
              <th>Usuário</th>
              <th>Página</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in logs" :key="log.id" @click="selectedLog = log" class="obs-log-row">
              <td class="obs-date obs-date--mono">{{ formatDate(log.created_at, true) }}</td>
              <td><span class="obs-level" :class="'obs-level--' + log.level">{{ log.level }}</span></td>
              <td><span class="obs-badge" :class="'obs-badge--' + log.category">{{ log.category }}</span></td>
              <td class="obs-msg" :title="log.message">{{ truncate(log.message, 100) }}</td>
              <td class="obs-date">{{ log.user_email || (log.user_id ? '#' + log.user_id : '–') }}</td>
              <td class="obs-url" :title="log.page_url">{{ shortUrl(log.page_url) }}</td>
              <td>
                <button v-if="log.stack || log.details" class="obs-btn obs-btn--xs" @click.stop="selectedLog = log">
                  Detalhes
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginação -->
      <div v-if="total > 0" class="obs-pagination">
        <span class="obs-pg-info">{{ total }} registros · página {{ filters.page }} de {{ Math.ceil(total / filters.limit) }}</span>
        <button class="obs-btn obs-btn--sm" :disabled="filters.page <= 1" @click="filters.page--; loadLogs()">‹ Anterior</button>
        <button class="obs-btn obs-btn--sm" :disabled="filters.page >= Math.ceil(total / filters.limit)" @click="filters.page++; loadLogs()">Próxima ›</button>
      </div>
    </div>
    </div>
    <!-- /aba erros -->

    <!-- ── Modal Detalhes ──────────────────────────────────── -->
    <div v-if="selectedLog" class="obs-modal-overlay" @click.self="selectedLog = null">
      <div class="obs-modal">
        <div class="obs-modal-head">
          <span class="obs-level" :class="'obs-level--' + selectedLog.level">{{ selectedLog.level }}</span>
          <span class="obs-badge" :class="'obs-badge--' + selectedLog.category">{{ selectedLog.category }}</span>
          <button class="obs-modal-close" @click="selectedLog = null">✕</button>
        </div>
        <div class="obs-modal-body">
          <div class="obs-detail-row"><strong>Mensagem</strong><p>{{ selectedLog.message }}</p></div>
          <div class="obs-detail-row" v-if="selectedLog.page_url"><strong>Página</strong><p>{{ selectedLog.page_url }}</p></div>
          <div class="obs-detail-row" v-if="selectedLog.user_email"><strong>Usuário</strong><p>{{ selectedLog.user_email }}</p></div>
          <div class="obs-detail-row" v-if="selectedLog.created_at"><strong>Data/Hora</strong><p>{{ formatDate(selectedLog.created_at, true) }}</p></div>
          <div class="obs-detail-row" v-if="selectedLog.details">
            <strong>Detalhes</strong>
            <pre class="obs-pre">{{ formatDetails(selectedLog.details) }}</pre>
          </div>
          <div class="obs-detail-row" v-if="selectedLog.stack">
            <strong>Stack Trace</strong>
            <pre class="obs-pre obs-pre--stack">{{ selectedLog.stack }}</pre>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

const API = process.env.VUE_APP_HOST_API_BACKEND || ''

export default {
  name: 'Observability',
  data() {
    return {
      loading: false,
      logsLoading: false,
      summary: {},
      logs: [],
      total: 0,
      selectedLog: null,
      filters: { level: '', category: '', search: '', page: 1, limit: 50 },
      categories: ['ui', 'api', 'player', 'promise', 'resource', 'upload', 'auth', 'general'],
      activeTab: 'erros',
      ahaLoading: false,
      ahaUsers: [],
      ahaStats: {},
      ahaFilter: '',
      ahaSteps: [
        { key: 'cadastrados',    label: 'Cadastrou conta',   icon: '📝', color: '#3B82F6' },
        { key: 'first_video',    label: 'Subiu 1º vídeo',    icon: '🎬', color: '#8B5CF6' },
        { key: 'first_chat',     label: 'Criou 1º chat',     icon: '💬', color: '#F59E0B' },
        { key: 'video_embedded', label: 'Vídeo foi tocado',  icon: '▶️', color: '#E86A10' },
        { key: 'chat_embedded',  label: 'Chat capturou lead',icon: '🎯', color: '#10B981' },
      ],
    }
  },
  computed: {
    filteredAhaUsers() {
      if (!this.ahaFilter) return this.ahaUsers
      switch (this.ahaFilter) {
        case 'activated':     return this.ahaUsers.filter(u => u.activated)
        case 'not_activated': return this.ahaUsers.filter(u => !u.activated)
        case 'no_video':      return this.ahaUsers.filter(u => !u.milestones.first_video.reached)
        case 'no_chat':       return this.ahaUsers.filter(u => !u.milestones.first_chat.reached)
        case 'no_play':       return this.ahaUsers.filter(u => u.milestones.first_video.reached && !u.milestones.video_embedded.reached)
        case 'no_lead':       return this.ahaUsers.filter(u => u.milestones.first_chat.reached && !u.milestones.chat_embedded.reached)
        default: return this.ahaUsers
      }
    },
  },
  created() {
    this.loadAll()
  },
  methods: {
    async loadAll() {
      this.loading = true
      await Promise.all([this.loadSummary(), this.loadLogs()])
      this.loading = false
    },
    async loadAhaMoments() {
      this.ahaLoading = true
      try {
        const { data } = await axios.get(`${API}/admin/aha-moments`)
        this.ahaUsers = data.users || []
        this.ahaStats = data.stats || {}
      } catch (e) {
        console.error('[obs] aha-moments error', e)
      } finally {
        this.ahaLoading = false
      }
    },
    async loadSummary() {
      try {
        const { data } = await axios.get(`${API}/admin/logs/summary`)
        this.summary = data
      } catch (e) { console.error('[obs] summary error', e) }
    },
    async loadLogs() {
      this.logsLoading = true
      try {
        const params = {
          page: this.filters.page,
          limit: this.filters.limit,
          ...(this.filters.level && { level: this.filters.level }),
          ...(this.filters.category && { category: this.filters.category }),
          ...(this.filters.search && { search: this.filters.search }),
        }
        const { data } = await axios.get(`${API}/admin/logs`, { params })
        this.logs = data.data
        this.total = data.total
      } catch (e) { console.error('[obs] logs error', e) }
      finally { this.logsLoading = false }
    },
    truncate(str, n) {
      if (!str) return '–'
      return str.length > n ? str.slice(0, n) + '…' : str
    },
    formatDate(dt, withTime = false) {
      if (!dt) return '–'
      const d = new Date(dt)
      const date = d.toLocaleDateString('pt-BR')
      if (!withTime) return date
      const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      return `${date} ${time}`
    },
    formatRelative(dt) {
      if (!dt) return '–'
      const d = new Date(dt)
      const now = new Date()
      const diff = Math.floor((now - d) / 1000)
      if (diff < 60) return 'agora'
      if (diff < 3600) return `há ${Math.floor(diff / 60)} min`
      if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`
      if (diff < 604800) return `há ${Math.floor(diff / 86400)} d`
      if (diff < 2592000) return `há ${Math.floor(diff / 604800)} sem`
      return d.toLocaleDateString('pt-BR')
    },
    shortUrl(url) {
      if (!url) return '–'
      try { return new URL(url).pathname.slice(0, 40) } catch { return url.slice(0, 40) }
    },
    initials(email) {
      if (!email) return '?'
      return email[0].toUpperCase()
    },
    barWidth(count) {
      if (!this.summary.by_category || !this.summary.by_category.length) return 0
      const max = this.summary.by_category[0].count
      return Math.round((count / max) * 100)
    },
    formatDetails(details) {
      if (!details) return ''
      try {
        const obj = typeof details === 'string' ? JSON.parse(details) : details
        return JSON.stringify(obj, null, 2)
      } catch { return String(details) }
    },
  },
}
</script>

<style scoped lang="scss">
.obs-page { padding: 0 0 60px; }

/* ── Tabs ─────────────────────────────────────── */
.obs-tabs {
  display: flex;
  gap: 4px;
  margin: 16px 0 24px;
  border-bottom: 2px solid rgba(255,255,255,0.08);
}
.obs-tab {
  padding: 10px 20px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255,255,255,0.45);
  cursor: pointer;
  border-bottom: 2.5px solid transparent;
  margin-bottom: -2px;
  transition: all 0.18s;
  font-family: inherit;
  &:hover { color: #E86A10; }
  &--active { color: #E86A10; border-bottom-color: #E86A10; }
}

/* ── Aha Funnel ──────────────────────────────── */
.obs-aha-funnel {
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.obs-aha-step {
  flex: 1;
  min-width: 160px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}
.obs-aha-step__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  opacity: 0.85;
}
.obs-aha-step__body { flex: 1; min-width: 0; }
.obs-aha-step__label {
  font-size: 11px;
  font-weight: 700;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}
.obs-aha-step__value { font-size: 22px; font-weight: 800; color: #e2e8f0; line-height: 1; }
.obs-aha-step__pct { font-size: 11px; color: rgba(255,255,255,0.35); margin-top: 2px; font-weight: 600; }
.obs-aha-arrow {
  position: absolute;
  right: -14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: rgba(255,255,255,0.2);
  z-index: 1;
}
@media (max-width: 1024px) { .obs-aha-arrow { display: none; } }

/* ── Aha Table ─────────────────────────────── */
.obs-th-center { text-align: center; }
.obs-row-activated { background: rgba(16,185,129,0.06); }
.obs-site-link {
  color: #E86A10;
  font-size: 12.5px;
  text-decoration: none;
  font-weight: 500;
  &:hover { text-decoration: underline; }
}
.obs-empty-cell { color: rgba(255,255,255,0.2); font-size: 12px; }
.obs-date-cell { color: rgba(255,255,255,0.45); font-size: 12.5px; font-weight: 500; }
.obs-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  &--ok { background: rgba(16,185,129,0.15); color: #10B981; }
  &--no { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.2); }
}
.obs-score {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.obs-score__bar {
  display: inline-block;
  width: 60px;
  height: 6px;
  background: rgba(255,255,255,0.08);
  border-radius: 3px;
  overflow: hidden;
}
.obs-score__fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #E86A10, #10B981);
  transition: width 0.3s;
}
.obs-score__num {
  font-size: 11px;
  font-weight: 700;
  color: rgba(255,255,255,0.45);
}

.obs-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 24px;
}
.obs-title { font-size: 1.45rem; font-weight: 700; color: #e2e8f0; margin: 0 0 4px; }
.obs-sub { font-size: 0.82rem; color: rgba(255,255,255,0.4); margin: 0; }

/* KPIs */
.obs-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
.obs-kpi {
  display: flex; align-items: center; gap: 14px;
  background: rgba(255,255,255,0.04); border-radius: 12px; padding: 18px 20px;
  border: 1px solid rgba(255,255,255,0.08);
}
.obs-kpi-icon { font-size: 1.6rem; }
.obs-kpi-value { font-size: 1.7rem; font-weight: 700; }
.obs-kpi-label { font-size: 0.73rem; color: rgba(255,255,255,0.4); margin-top: 2px; }
.obs-kpi--red .obs-kpi-value { color: #f87171; }
.obs-kpi--orange .obs-kpi-value { color: #E86A10; }
.obs-kpi--blue .obs-kpi-value { color: #60a5fa; }
.obs-kpi--purple .obs-kpi-value { color: #a78bfa; }

/* Grid */
.obs-grid { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 16px; margin-bottom: 20px; }

/* Cards */
.obs-card {
  background: rgba(255,255,255,0.04); border-radius: 12px; border: 1px solid rgba(255,255,255,0.08);
  overflow: hidden;
}
.obs-card--wide { grid-column: 1 / -1; }
.obs-card--full { margin-top: 4px; }
.obs-card-head {
  padding: 16px 20px; border-bottom: 1px solid rgba(255,255,255,0.07);
}
.obs-card-head--flex {
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;
}
.obs-card-title { font-size: 0.88rem; font-weight: 600; color: #e2e8f0; }

/* Tables */
.obs-table-wrap { overflow-x: auto; }
.obs-table {
  width: 100%; border-collapse: collapse; font-size: 0.8rem;
  th {
    padding: 10px 14px; text-align: left; font-size: 0.73rem; font-weight: 600;
    color: rgba(255,255,255,0.4); background: rgba(255,255,255,0.03); border-bottom: 1px solid rgba(255,255,255,0.07);
    white-space: nowrap;
  }
  td { padding: 10px 14px; border-bottom: 1px solid rgba(255,255,255,0.05); vertical-align: middle; color: #cbd5e1; }
  tr:last-child td { border-bottom: none; }
}
.obs-table--logs tbody tr { cursor: pointer; }
.obs-table--logs tbody tr:hover td { background: rgba(255,255,255,0.04); }
.obs-log-row { transition: background 0.1s; }
.obs-rank { color: rgba(255,255,255,0.3); font-weight: 600; width: 30px; }
.obs-count { text-align: right; font-weight: 600; width: 80px; }
.obs-msg { max-width: 380px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.obs-url { max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: rgba(255,255,255,0.35); font-size: 0.75rem; }
.obs-date { white-space: nowrap; color: rgba(255,255,255,0.35); font-size: 0.75rem; }
.obs-date--mono { font-family: monospace; }

/* Level badges */
.obs-level {
  display: inline-block; padding: 2px 8px; border-radius: 6px;
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
}
.obs-level--error { background: rgba(248,113,113,0.15); color: #f87171; }
.obs-level--warn  { background: rgba(251,191,36,0.15); color: #fbbf24; }
.obs-level--info  { background: rgba(96,165,250,0.15); color: #60a5fa; }

/* Category badges */
.obs-badge {
  display: inline-block; padding: 2px 8px; border-radius: 20px;
  font-size: 0.68rem; font-weight: 600; background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.6);
}
.obs-badge--ui       { background: rgba(167,139,250,0.18); color: #a78bfa; }
.obs-badge--api      { background: rgba(52,211,153,0.18); color: #34d399; }
.obs-badge--player   { background: rgba(251,191,36,0.18); color: #fbbf24; }
.obs-badge--promise  { background: rgba(248,113,113,0.18); color: #f87171; }
.obs-badge--resource { background: rgba(96,165,250,0.18); color: #60a5fa; }
.obs-badge--upload   { background: rgba(244,114,182,0.18); color: #f472b6; }
.obs-badge--auth     { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); }

/* Category bar chart */
.obs-cat-list { padding: 12px 20px; display: flex; flex-direction: column; gap: 10px; }
.obs-cat-row { display: flex; align-items: center; gap: 10px; }
.obs-cat-name { min-width: 90px; }
.obs-cat-bar-wrap { flex: 1; height: 6px; background: rgba(255,255,255,0.07); border-radius: 4px; overflow: hidden; }
.obs-cat-bar { height: 100%; background: #E86A10; border-radius: 4px; transition: width 0.4s; }
.obs-cat-count { min-width: 32px; text-align: right; font-size: 0.78rem; font-weight: 600; color: rgba(255,255,255,0.45); }

/* User list */
.obs-user-list { list-style: none; margin: 0; padding: 12px 20px; display: flex; flex-direction: column; gap: 10px; }
.obs-user-row { display: flex; align-items: center; gap: 12px; }
.obs-user-avatar {
  width: 32px; height: 32px; border-radius: 50%; background: rgba(167,139,250,0.2); color: #a78bfa;
  display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.85rem; flex-shrink: 0;
}
.obs-user-email { font-size: 0.85rem; font-weight: 700; color: #e2e8f0; }
.obs-user-email-text { font-size: 0.74rem; color: #818cf8; font-weight: 500; word-break: break-all; }
.obs-user-meta { font-size: 0.72rem; color: rgba(255,255,255,0.35); }

/* Filters */
.obs-filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.obs-select, .obs-input {
  height: 32px; padding: 0 10px; border: 1px solid rgba(255,255,255,0.1); border-radius: 7px;
  font-size: 0.78rem; color: #e2e8f0; background: rgba(255,255,255,0.06);
  option { background: #1e2a3a; color: #e2e8f0; }
  &:focus { outline: none; border-color: #E86A10; }
}
.obs-input { min-width: 180px; }
.obs-input::placeholder { color: rgba(255,255,255,0.3); }

/* Buttons */
.obs-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 14px; border-radius: 8px; font-size: 0.8rem; font-weight: 600;
  cursor: pointer; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.06); color: #cbd5e1;
  transition: all 0.15s;
  &:hover:not(:disabled) { background: rgba(255,255,255,0.1); color: #e2e8f0; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}
.obs-btn--ghost { background: transparent; border-color: rgba(255,255,255,0.08); }
.obs-btn--sm { padding: 5px 10px; font-size: 0.75rem; }
.obs-btn--xs { padding: 2px 8px; font-size: 0.7rem; }

/* Pagination */
.obs-pagination { display: flex; align-items: center; gap: 8px; padding: 14px 20px; border-top: 1px solid rgba(255,255,255,0.07); }
.obs-pg-info { font-size: 0.78rem; color: rgba(255,255,255,0.35); flex: 1; }

/* Empty / loading */
.obs-empty { padding: 32px; text-align: center; color: rgba(255,255,255,0.3); font-size: 0.85rem; }
.obs-loading { padding: 32px; text-align: center; color: rgba(255,255,255,0.3); font-size: 0.85rem; }

/* Modal */
.obs-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.65); z-index: 9999;
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.obs-modal {
  background: #111827; border: 1px solid rgba(255,255,255,0.1); border-radius: 14px; max-width: 680px; width: 100%;
  max-height: 80vh; overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6);
}
.obs-modal-head {
  display: flex; align-items: center; gap: 8px; padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.obs-modal-close {
  margin-left: auto; background: none; border: none; font-size: 1rem;
  cursor: pointer; color: rgba(255,255,255,0.4);
  &:hover { color: #f87171; }
}
.obs-modal-body { padding: 20px; display: flex; flex-direction: column; gap: 14px; }
.obs-detail-row {
  strong { display: block; font-size: 0.75rem; color: rgba(255,255,255,0.4); margin-bottom: 4px; }
  p { margin: 0; font-size: 0.85rem; word-break: break-all; color: #cbd5e1; }
}
.obs-pre {
  background: rgba(0,0,0,0.4); color: #e2e8f0; border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 12px;
  font-size: 0.72rem; overflow-x: auto; margin: 0; white-space: pre-wrap; word-break: break-all;
}
.obs-pre--stack { max-height: 300px; }

/* Spin animation */
@keyframes spin { to { transform: rotate(360deg); } }
.obs-spin { animation: spin 0.8s linear infinite; }

@media (max-width: 900px) {
  .obs-kpis { grid-template-columns: repeat(2, 1fr); }
  .obs-grid { grid-template-columns: 1fr; }
  .obs-card--wide { grid-column: 1; }
}
</style>
